import React from 'react';
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageSquare, Plus, Settings, Trash2, X } from "lucide-react";
import { useTranslations } from "@/hooks/useTranslations";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Channel } from './types';
import { useAuth } from '@/components/AuthProvider';
import { useProfile } from '@/hooks/useProfile';
import { cn } from '@/lib/utils';

type User = {
  id: string;
  name: string;
  avatar_url: string | null;
};

type ChannelListProps = {
  channels: Channel[];
  directMessages: User[];
  loading: boolean;
  selectedChannelId: string | null;
  selectedUserId: string | null;
  unreadConversations: Set<string>;
  getConversationId: (type: 'channel' | 'dm', id: string) => string;
  onSelectChannel: (channel: Channel | null) => void;
  onSelectUser: (userId: string | null) => void;
  onOpenCreateChannel: () => void;
  onOpenStudentList: () => void;
  onOpenEditChannel: (channel: Channel) => void;
  onOpenDeleteChannel: (channel: Channel) => void;
  onRemoveDirectContact: (userId: string) => void;
};

export const ChannelList = ({ 
  channels,
  directMessages,
  loading,
  selectedChannelId,
  selectedUserId,
  unreadConversations,
  getConversationId,
  onSelectChannel, 
  onSelectUser,
  onOpenCreateChannel,
  onOpenStudentList,
  onOpenEditChannel,
  onOpenDeleteChannel,
  onRemoveDirectContact
}: ChannelListProps) => {
  const { t } = useTranslations('messages');
  const { user } = useAuth();
  const { profile } = useProfile(user);
  const isTrainer = profile?.is_trainer;

  if (loading) {
    return (
      <div className="p-6 text-sm text-center text-muted-foreground">{t('loading')}</div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Channels */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex justify-between items-center px-4 py-2.5">
          <h3 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">{t('channels')}</h3>
          {isTrainer && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-primary"
              onClick={onOpenCreateChannel}
              title={t('createChannel')}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        <ScrollArea className="flex-1">
          <div className="px-2 space-y-0.5">
            {channels.map((channel) => {
              const conversationId = getConversationId('channel', channel.id);
              const isUnread = unreadConversations.has(conversationId);
              const isSelected = selectedChannelId === channel.id;
              return (
                <div 
                  key={channel.id} 
                  className={cn(
                    "flex items-center justify-between w-full gap-1 px-3 py-2 rounded-lg cursor-pointer group transition-all duration-150",
                    isSelected 
                      ? "bg-primary/10 text-primary shadow-sm" 
                      : "hover:bg-accent/60 text-foreground"
                  )}
                  onClick={() => onSelectChannel(isSelected ? null : channel)}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="text-muted-foreground text-sm font-medium">#</span>
                    {isUnread && <span className="h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />}
                    <span className={cn("text-sm truncate", isUnread && "font-semibold", isSelected && "font-medium")}>
                      {channel.name}
                    </span>
                  </div>
                  {isTrainer && (
                    <div className="flex items-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-foreground"
                        onClick={(e) => { e.stopPropagation(); onOpenEditChannel(channel); }}
                        title={t('editChannel')}
                      >
                        <Settings className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); onOpenDeleteChannel(channel); }}
                        title={t('deleteChannel')}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
            {channels.length === 0 && (
              <div className="px-3 py-3 text-xs text-muted-foreground text-center">
                {t('noChannels')}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Direct Messages */}
      <div className="flex-1 min-h-0 border-t border-border flex flex-col">
        <div className="flex justify-between items-center px-4 py-2.5">
          <h3 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">
            {t('directMessages')}
          </h3>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-primary"
            onClick={onOpenStudentList}
            title={t('startChat')}
          >
            <MessageSquare className="h-3.5 w-3.5" />
          </Button>
        </div>

        <ScrollArea className="flex-1">
          <div className="px-2 space-y-0.5">
            {directMessages.map((contact) => {
              const conversationId = getConversationId('dm', contact.id);
              const isUnread = unreadConversations.has(conversationId);
              const isSelected = selectedUserId === contact.id;
              return (
                <div 
                  key={contact.id} 
                  className={cn(
                    "flex items-center justify-between w-full gap-1 px-3 py-2 rounded-lg cursor-pointer group transition-all duration-150",
                    isSelected 
                      ? "bg-primary/10 text-primary shadow-sm" 
                      : "hover:bg-accent/60 text-foreground"
                  )}
                  onClick={() => onSelectUser(isSelected ? null : contact.id)}
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <Avatar className="h-7 w-7 flex-shrink-0">
                      <AvatarImage src={contact.avatar_url || ''} />
                      <AvatarFallback className="text-[10px] bg-muted">
                        {contact.name?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex items-center gap-1.5 min-w-0">
                      {isUnread && <span className="h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />}
                      <span className={cn(
                        "text-sm truncate",
                        isUnread && "font-semibold",
                        isSelected && "font-medium"
                      )}>
                        {contact.name}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    onClick={(e) => { e.stopPropagation(); onRemoveDirectContact(contact.id); }}
                    title={t('removeFromList')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              );
            })}
            {directMessages.length === 0 && (
              <div className="px-3 py-3 text-xs text-muted-foreground text-center">
                {t('noDirectMessages')}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};
